import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Trash2, Gift, Percent, XCircle, Banknote, CreditCard, Wallet, Clock, Store, ConciergeBell, ShoppingBag, Users2 } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const eur = (c) => `€ ${(((c ?? 0)) / 100).toFixed(2)}`;
const GOLD = "#C9A24B";

const TYPES = [
  { key: "counter", label: "Banco", icon: Store },
  { key: "takeaway", label: "Asporto", icon: ShoppingBag },
  { key: "table", label: "Tavolo", icon: ConciergeBell },
];

export default function Pos() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [tables, setTables] = useState([]);
  const [cash, setCash] = useState({ open: false });
  const [activeCat, setActiveCat] = useState("all");
  const [orderType, setOrderType] = useState("counter");
  const [tableId, setTableId] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [modifierPick, setModifierPick] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const canManage = user?.role === "admin" || user?.role === "manager";

  const load = useCallback(async () => {
    try {
      const [c, cat, tb] = await Promise.all([
        api.get("/pos/config"), api.get("/pos/catalog"), api.get("/tables"),
      ]);
      setConfig(c.data); setCatalog(cat.data); setTables(tb.data);
      const cc = await api.get("/pos/cash/current"); setCash(cc.data);
    } catch (e) {
      if (e.response?.status === 403) setConfig({ disabled: true });
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const perms = config?.permissions || {};
  const feats = config?.features || {};

  const dishes = useMemo(() => {
    const all = catalog?.dishes || [];
    if (activeCat === "all") return all;
    return all.filter((d) => String(d.category_id) === String(activeCat));
  }, [catalog, activeCat]);

  const itemsSpec = (items) => (items || []).map((i) => ({ dish_id: i.dish_id, quantity: i.quantity, item_id: i.item_id, notes: i.notes || "", modifiers: (i.modifiers || []).map((m) => ({ modifier_id: m.modifier_id })) }));

  const newOrder = async () => {
    if (orderType === "table" && !tableId) { toast.error("Seleziona un tavolo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pos/orders", { order_type: orderType, table_id: tableId || "", items: [] });
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sync = async (spec) => {
    const { data } = await api.post(`/pos/orders/${order.id}/items`, { items: spec });
    setOrder(data);
  };

  const _addItem = async (dish, modifierIds) => {
    const spec = [...itemsSpec(order.items), { dish_id: dish.id, quantity: 1, modifiers: (modifierIds || []).map((id) => ({ modifier_id: id })) }];
    try { await sync(spec); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addDish = async (dish) => {
    if (!order) { toast.error("Apri prima un conto"); return; }
    const groupIds = dish.modifier_group_ids || [];
    if (groupIds.length) {
      const groups = (catalog.modifier_groups || []).filter((g) => groupIds.includes(g.id));
      if (groups.length) { setModifierPick({ dish, groups, selected: [] }); return; }
    }
    const items = order.items || [];
    const existing = items.find((i) => i.dish_id === dish.id && (!i.modifiers || i.modifiers.length === 0) && !i.voided && !i.comp && !i.line_discount);
    let spec = itemsSpec(items);
    if (existing) spec = spec.map((s) => (s.item_id === existing.item_id ? { ...s, quantity: s.quantity + 1 } : s));
    else spec = [...spec, { dish_id: dish.id, quantity: 1 }];
    try { await sync(spec); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeQty = async (item, delta) => {
    let spec = itemsSpec(order.items);
    spec = spec.map((s) => (s.item_id === item.item_id ? { ...s, quantity: s.quantity + delta } : s)).filter((s) => s.quantity > 0);
    try { await sync(spec); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeItem = async (item) => {
    const spec = itemsSpec(order.items).filter((s) => s.item_id !== item.item_id);
    try { await sync(spec); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const lineAction = async (fn) => { setBusy(true); try { const { data } = await fn(); setOrder(data.order || data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; } finally { setBusy(false); } };
  const compItem = (item) => setPrompt({ title: "Omaggia prodotto", message: `Omaggia "${item.name}"? Il prezzo va a € 0,00 ma il magazzino viene comunque scaricato.`, reason: true, requireReason: true, confirmLabel: "Omaggia", danger: true, onConfirm: (v) => lineAction(() => api.post(`/pos/orders/${order.id}/items/${item.item_id}/comp`, { reason: v.reason })) });
  const voidItem = (item) => { const prepared = ["preparing", "ready", "served"].includes(item.status); setPrompt({ title: "Annulla riga", message: prepared ? `"${item.name}" risulta già preparata: verrà registrato il consumo (waste), nessun ricavo. Motivo obbligatorio.` : `Annullare "${item.name}"?`, reason: true, requireReason: prepared, confirmLabel: "Annulla riga", danger: true, onConfirm: (v) => lineAction(() => api.post(`/pos/orders/${order.id}/items/${item.item_id}/void`, { reason: v.reason || "" })) }); };
  const lineDiscount = (item) => setPrompt({ title: "Sconto riga", message: `Sconto percentuale su "${item.name}"`, amount: true, amountLabel: "Sconto %", reason: true, confirmLabel: "Applica sconto", onConfirm: (v) => lineAction(() => api.post(`/pos/orders/${order.id}/items/${item.item_id}/discount`, { percentage: parseFloat(v.amount) || 0, reason: v.reason || "sconto riga" })) });
  const orderDiscount = () => setPrompt({ title: "Sconto sul conto", message: "Sconto percentuale sull'intero conto", amount: true, amountLabel: "Sconto %", reason: true, confirmLabel: "Applica sconto", onConfirm: (v) => lineAction(() => api.post(`/pos/orders/${order.id}/discount`, { percentage: parseFloat(v.amount) || 0, reason: v.reason || "sconto conto" })) });

  const resetOrder = () => { setOrder(null); setTableId(""); load(); };

  // -------- cash --------
  const openCash = () => setPrompt({ title: "Apri cassa", message: "Inserisci il fondo iniziale di cassa.", amount: true, amountLabel: "Fondo €", confirmLabel: "Apri cassa", onConfirm: async (v) => { await api.post("/pos/cash/open", { opening_cash: parseFloat(v.amount) || 0 }); toast.success("Cassa aperta"); load(); } });
  const closeCash = () => setPrompt({ title: "Chiudi cassa", message: `Contanti attesi: ${eur(cash.expected_cash_cents)}. Inserisci i contanti realmente in cassa (dichiarati). La differenza verrà registrata.`, amount: true, amountLabel: "Dichiarati €", confirmLabel: "Chiudi cassa", danger: true, onConfirm: async (v) => { const { data } = await api.post("/pos/cash/close", { declared_cash: parseFloat(v.amount) || 0 }); toast.success(`Cassa chiusa · differenza € ${data.difference.toFixed(2)}`); load(); } });
  const cashMove = (type) => setPrompt({ title: type === "deposit" ? "Versamento" : "Prelievo", message: type === "deposit" ? "Aggiungi contante al fondo cassa." : "Preleva contante dalla cassa.", amount: true, amountLabel: "Importo €", reason: true, requireReason: true, confirmLabel: "Registra", onConfirm: async (v) => { await api.post(`/pos/cash/${type}`, { amount: parseFloat(v.amount) || 0, reason: v.reason }); toast.success("Movimento registrato"); load(); } });

  if (config?.disabled || (config && !feats.pos_enabled)) {
    return (
      <div className="p-8" data-testid="pos-disabled">
        <h1 className="font-dash text-2xl font-semibold mb-2">Cassa POS non abilitata</h1>
        <p className="text-sm text-muted-foreground">Abilita <b>pos_enabled</b> nelle Impostazioni per usare la cassa.</p>
      </div>
    );
  }
  if (!config || !catalog) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const balance = order ? (order.total_cents ?? 0) - (order.payments || []).filter((p) => p.status === "confirmed").reduce((a, p) => a + (p.amount_cents || 0), 0) : 0;

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)] -m-4 sm:-m-6 bg-[#14110E] text-white" data-testid="pos-page">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="font-dash text-lg font-semibold" style={{ color: GOLD }}>CASSA</span>
          <span className="text-xs text-white/60" data-testid="pos-operator">{user?.name || user?.email}</span>
          {order && <Badge className="rounded-full bg-white/10 text-white" data-testid="pos-order-code">{order.order_code} · {order.order_type}{order.table_number ? ` · T${order.table_number}` : ""}</Badge>}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {canManage && <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={() => setMergeOpen(true)} data-testid="merge-tables-btn"><Users2 className="w-3.5 h-3.5 mr-1" />Unisci tavoli</Button>}
          <Clock className="w-3.5 h-3.5 text-white/50" />
          <span className="text-white/60">{new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
          {feats.cash_management_enabled && (
            cash.open
              ? <Badge className="rounded-full bg-emerald-500/20 text-emerald-300" data-testid="cash-status-open">Cassa aperta · attesi {eur(cash.expected_cash_cents)}</Badge>
              : <Badge className="rounded-full bg-red-500/20 text-red-300" data-testid="cash-status-closed">Cassa chiusa</Badge>
          )}
        </div>
      </div>

      {/* cash bar */}
      {feats.cash_management_enabled && (
        <div className="flex gap-2 px-4 py-1.5 border-b border-white/10 text-xs flex-wrap">
          {!cash.open
            ? <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={openCash} data-testid="cash-open-btn">Apri cassa</Button>
            : <>
                <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={() => cashMove("deposit")} data-testid="cash-deposit-btn">Versamento</Button>
                <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={() => cashMove("withdrawal")} data-testid="cash-withdrawal-btn">Prelievo</Button>
                <Button size="sm" variant="secondary" className="rounded-full h-7" onClick={closeCash} data-testid="cash-close-btn">Chiudi cassa</Button>
              </>}
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* LEFT categories */}
        <div className="w-28 sm:w-40 shrink-0 border-r border-white/10 overflow-y-auto p-2 space-y-1" data-testid="pos-categories">
          <button className={`w-full text-left px-3 py-2 rounded-lg text-sm ${activeCat === "all" ? "bg-white/15" : "hover:bg-white/5"}`} onClick={() => setActiveCat("all")} data-testid="cat-all">Tutti</button>
          {(catalog.categories || []).map((c) => (
            <button key={c.id} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${String(activeCat) === String(c.id) ? "bg-white/15" : "hover:bg-white/5"}`} onClick={() => setActiveCat(c.id)} data-testid={`cat-${c.id}`}>{c.name}</button>
          ))}
        </div>

        {/* CENTER products */}
        <div className="flex-1 overflow-y-auto p-3">
          {!order ? (
            <div className="max-w-md mx-auto mt-8 space-y-4" data-testid="pos-new-order">
              <h2 className="font-dash text-xl">Nuovo conto</h2>
              <div className="flex gap-2">
                {TYPES.map((t) => (
                  <button key={t.key} onClick={() => setOrderType(t.key)} className={`flex-1 py-4 rounded-xl border ${orderType === t.key ? "border-[#C9A24B] bg-white/10" : "border-white/15"}`} data-testid={`type-${t.key}`}>
                    <t.icon className="w-5 h-5 mx-auto mb-1" style={{ color: GOLD }} /><span className="text-sm">{t.label}</span>
                  </button>
                ))}
              </div>
              {orderType === "table" && (
                <select className="w-full h-11 rounded-lg bg-white/10 border border-white/15 px-3 text-sm" value={tableId} onChange={(e) => setTableId(e.target.value)} data-testid="pos-table-select">
                  <option value="">Seleziona tavolo…</option>
                  {tables.map((t) => <option key={t.id} value={t.id} className="text-black">{t.name || `Tavolo ${t.number}`}</option>)}
                </select>
              )}
              <Button className="w-full rounded-full h-12" style={{ backgroundColor: GOLD, color: "#14110E" }} onClick={newOrder} disabled={busy} data-testid="pos-start-order">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apri conto"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="pos-product-grid">
              {dishes.map((d) => (
                <button key={d.id} onClick={() => addDish(d)} className="text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors active:scale-95" data-testid={`product-${d.id}`}>
                  <p className="text-sm font-medium leading-tight line-clamp-2">{d.name}</p>
                  <p className="text-xs mt-1" style={{ color: GOLD }}>€ {(d.price || 0).toFixed(2)}</p>
                </button>
              ))}
              {dishes.length === 0 && <p className="text-white/50 text-sm col-span-full">Nessun prodotto in questa categoria.</p>}
            </div>
          )}
        </div>

        {/* RIGHT conto */}
        {order && (
          <div className="w-72 sm:w-80 shrink-0 border-l border-white/10 flex flex-col" data-testid="pos-conto">
            <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
              <span className="text-sm font-medium">Conto</span>
              <Button size="sm" variant="ghost" className="h-7 text-white/60 hover:text-white" onClick={resetOrder} data-testid="pos-close-conto">Chiudi vista</Button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {(order.items || []).map((it) => (
                <div key={it.item_id} className={`rounded-lg p-2 ${it.voided ? "opacity-40 line-through" : "bg-white/5"}`} data-testid={`conto-item-${it.item_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm flex-1 truncate">{it.name}{it.comp && <Badge className="ml-1 bg-purple-500/30 text-purple-200 text-[10px] rounded-full">OMAGGIO</Badge>}</span>
                    <span className="text-sm" style={{ color: GOLD }}>{eur(it.line_total_cents)}</span>
                  </div>
                  {!it.voided && (
                    <div className="flex items-center gap-1 mt-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => changeQty(it, -1)} data-testid={`qty-minus-${it.item_id}`}><Minus className="w-3 h-3" /></Button>
                      <span className="text-xs w-5 text-center" data-testid={`qty-${it.item_id}`}>{it.quantity}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => changeQty(it, 1)} data-testid={`qty-plus-${it.item_id}`}><Plus className="w-3 h-3" /></Button>
                      <div className="flex-1" />
                      {feats.discount_enabled && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => lineDiscount(it)} data-testid={`line-disc-${it.item_id}`}><Percent className="w-3 h-3" /></Button>}
                      {perms.comp && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => compItem(it)} data-testid={`comp-${it.item_id}`}><Gift className="w-3 h-3" /></Button>}
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-300" onClick={() => voidItem(it)} data-testid={`void-${it.item_id}`}><XCircle className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-300" onClick={() => removeItem(it)} data-testid={`remove-${it.item_id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  )}
                </div>
              ))}
              {(order.items || []).length === 0 && <p className="text-white/40 text-sm text-center mt-6">Tocca i prodotti per aggiungerli</p>}
            </div>
            <div className="border-t border-white/10 p-3 space-y-2">
              {order.order_discount_amount_cents > 0 && <div className="flex justify-between text-xs text-white/60"><span>Sconto conto</span><span>- {eur(order.order_discount_amount_cents)}</span></div>}
              <div className="flex justify-between text-lg font-semibold"><span>Totale</span><span style={{ color: GOLD }} data-testid="conto-total">{eur(order.total_cents)}</span></div>
              {balance !== (order.total_cents ?? 0) && <div className="flex justify-between text-sm text-amber-300"><span>Residuo</span><span data-testid="conto-balance">{eur(balance)}</span></div>}
              <div className="flex gap-2">
                {feats.discount_enabled && <Button variant="secondary" className="rounded-full flex-1 h-9" onClick={orderDiscount} data-testid="order-discount-btn">Sconto</Button>}
                <Button className="rounded-full flex-[2] h-11 font-semibold" style={{ backgroundColor: "#16a34a", color: "white" }} disabled={(order.items || []).filter((i) => !i.voided).length === 0} onClick={() => setPayOpen(true)} data-testid="incassa-btn">
                  <Banknote className="w-4 h-4 mr-1" /> INCASSA
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {order && <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={order} balance={balance} feats={feats} onPaid={resetOrder} refresh={async () => { const { data } = await api.get(`/orders/${order.id}`); setOrder(data); }} />}
      <PromptDialog cfg={prompt} onClose={() => setPrompt(null)} />
      {modifierPick && <ModifierDialog pick={modifierPick} onClose={() => setModifierPick(null)} onConfirm={(ids) => { const d = modifierPick.dish; setModifierPick(null); _addItem(d, ids); }} />}
      <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen} onDone={() => { setMergeOpen(false); load(); }} />
    </div>
  );
}

function PaymentDialog({ open, onOpenChange, order, balance, feats, onPaid, refresh }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [received, setReceived] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setAmount((balance / 100).toFixed(2)); setReceived(""); setMethod("cash"); } }, [open, balance]);

  const methods = [
    { k: "cash", label: "Contanti", icon: Banknote },
    { k: "card", label: "Carta", icon: CreditCard },
    { k: "sumup", label: "SumUp", icon: Wallet },
    { k: "other", label: "Altro", icon: Wallet },
  ];
  const change = method === "cash" && received ? Math.max(0, Math.round(parseFloat(received) * 100) - Math.round((parseFloat(amount) || 0) * 100)) : 0;

  const addPayment = async () => {
    setBusy(true);
    try {
      const body = { method, amount: parseFloat(amount) || 0 };
      if (method === "cash" && received) body.cash_received = parseFloat(received);
      const { data } = await api.post(`/pos/orders/${order.id}/payments`, body, { headers: { "Idempotency-Key": uid() } });
      if (data.payment_status === "paid") toast.success("Conto saldato"); else toast.success("Pagamento registrato");
      await refresh();
      setReceived("");
      setAmount((Math.max(0, data.balance_cents || 0) / 100).toFixed(2));
      if (data.balance_cents <= 0) await doCheckout();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doCheckout = async () => {
    try {
      const { data } = await api.post(`/pos/orders/${order.id}/checkout`, { payments: [] }, { headers: { "Idempotency-Key": uid() } });
      if (data.status === "paid_closed") { toast.success("Conto chiuso · vendita registrata"); onOpenChange(false); onPaid(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const oneTap = async () => {
    setBusy(true);
    try {
      const body = { method };
      if (method === "cash" && received) body.cash_received = parseFloat(received);
      const { data } = await api.post(`/pos/orders/${order.id}/checkout`, { payments: [body] }, { headers: { "Idempotency-Key": uid() } });
      if (data.status === "paid_closed") { toast.success("Conto chiuso · vendita registrata"); onOpenChange(false); onPaid(); }
      else { toast.info("Pagamento parziale registrato"); await refresh(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Incasso · residuo {eur(balance)}</DialogTitle>
          <DialogDescription className="text-xs">Il pagamento registra l'incasso; la chiusura genera vendita e scarico magazzino. Nessuna emissione fiscale.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-2">
          {methods.map((m) => (
            <button key={m.k} onClick={() => setMethod(m.k)} className={`py-3 rounded-xl border text-xs flex flex-col items-center gap-1 ${method === m.k ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`paymethod-${m.k}`}>
              <m.icon className="w-4 h-4" />{m.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <div><label className="text-xs text-muted-foreground">Importo €</label><Input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" /></div>
          {method === "cash" && (
            <div>
              <label className="text-xs text-muted-foreground">Contante ricevuto €</label>
              <Input value={received} onChange={(e) => setReceived(e.target.value)} placeholder="opzionale" data-testid="pay-received" />
              {change > 0 && <p className="text-sm mt-1 font-medium text-emerald-600" data-testid="pay-change">Resto: {eur(change)}</p>}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {feats.split_payment_enabled && (
            <Button variant="outline" className="rounded-full" onClick={addPayment} disabled={busy} data-testid="pay-partial-btn">Aggiungi pagamento (parziale)</Button>
          )}
          <Button className="rounded-full flex-1" style={{ backgroundColor: "#16a34a", color: "white" }} onClick={oneTap} disabled={busy} data-testid="pay-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Incassa ${eur(Math.round((parseFloat(amount) || 0) * 100))}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromptDialog({ cfg, onClose }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmount(""); setReason(""); }, [cfg]);
  if (!cfg) return null;
  const submit = async () => {
    if (cfg.requireReason && !reason.trim()) { toast.error("Il motivo è obbligatorio"); return; }
    setBusy(true);
    try { await cfg.onConfirm({ amount, reason: reason.trim() }); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!cfg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-sm" data-testid="prompt-dialog">
        <DialogHeader><DialogTitle>{cfg.title}</DialogTitle><DialogDescription className="text-xs pt-1">{cfg.message}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {cfg.amount && <div><label className="text-xs text-muted-foreground">{cfg.amountLabel || "Importo"}</label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid="prompt-amount" /></div>}
          {cfg.reason && <div><label className="text-xs text-muted-foreground">Motivo{cfg.requireReason ? " (obbligatorio)" : ""}</label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="prompt-reason" /></div>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose} disabled={busy} data-testid="prompt-cancel">Annulla</Button>
          <Button className="rounded-full" variant={cfg.danger ? "destructive" : "default"} onClick={submit} disabled={busy} data-testid="prompt-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (cfg.confirmLabel || "Conferma")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModifierDialog({ pick, onClose, onConfirm }) {
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="modifier-dialog">
        <DialogHeader><DialogTitle>{pick.dish.name}</DialogTitle><DialogDescription className="text-xs">Seleziona i modificatori</DialogDescription></DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto">
          {pick.groups.map((g) => (
            <div key={g.id}>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{g.name}</p>
              <div className="grid grid-cols-2 gap-2">
                {(g.modifiers || []).map((m) => (
                  <button key={m.id} onClick={() => toggle(m.id)} className={`p-2 rounded-lg border text-sm text-left ${sel.includes(m.id) ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`modifier-${m.id}`}>
                    {m.name}{m.price_delta ? <span className="text-xs text-muted-foreground"> +€{Number(m.price_delta).toFixed(2)}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="modifier-cancel">Annulla</Button>
          <Button className="rounded-full" onClick={() => onConfirm(sel)} data-testid="modifier-confirm">Aggiungi al conto</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ open, onOpenChange, onDone }) {
  const [orders, setOrders] = useState([]);
  const [target, setTarget] = useState("");
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTarget(""); setSources([]); api.get("/pos/orders").then(({ data }) => setOrders(data.filter((o) => !(o.payments || []).length))); } }, [open]);
  const toggle = (id) => setSources((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const doMerge = async () => {
    if (!target || sources.length === 0) { toast.error("Seleziona destinazione e almeno un'origine"); return; }
    setBusy(true);
    try { await api.post("/pos/orders/merge", { target_order_id: target, source_order_ids: sources.filter((s) => s !== target) }); toast.success("Tavoli uniti"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const label = (o) => `${o.order_code}${o.table_number ? ` · T${o.table_number}` : ` · ${o.order_type}`} · ${(o.items || []).filter((i) => !i.voided).length} righe · € ${((o.total_cents || 0) / 100).toFixed(2)}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="merge-dialog">
        <DialogHeader><DialogTitle>Unisci tavoli / conti</DialogTitle><DialogDescription className="text-xs">Sposta tutte le righe dei conti origine nel conto destinazione. Conti con pagamenti già presenti non sono selezionabili. Operazione tracciata (audit).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Conto destinazione</label>
            <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="merge-target">
              <option value="">Seleziona…</option>
              {orders.map((o) => <option key={o.id} value={o.id}>{label(o)}</option>)}
            </select>
          </div>
          <div><p className="text-xs text-muted-foreground mb-1">Conti da unire (origine)</p>
            <div className="space-y-1 max-h-[40vh] overflow-y-auto">
              {orders.filter((o) => o.id !== target).map((o) => (
                <button key={o.id} onClick={() => toggle(o.id)} className={`w-full text-left p-2 rounded-lg border text-sm ${sources.includes(o.id) ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`merge-source-${o.id}`}>{label(o)}</button>
              ))}
              {orders.length === 0 && <p className="text-xs text-muted-foreground">Nessun conto POS aperto.</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={busy} data-testid="merge-cancel">Annulla</Button>
          <Button className="rounded-full" onClick={doMerge} disabled={busy} data-testid="merge-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unisci"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
